import React, { useEffect, useState, useMemo, useCallback } from "react";
import useWindowWidth from "../../Hooks/useWindowWidth";
import Button from "../UI/Buttons/Button";
import DropDown from "../UI/Inputs/DropDown";
import {
  fetchCityList,
  fetchAllSurveyList,
  fetchLocationWiseSurveyandYearList,
  fetchIMISurveyList,
} from "../../Utils/cityProfileUtils";
import useApi from "../../Hooks/useApi";
import { useNavigate } from "react-router-dom";
import { isAuthenticated } from "../../Helpers/authHelper";
import DropDownGrouped from "../UI/Inputs/DropDownGrouped";
import ExportButton from "../../Utils/CityDataPdfExport/exportButton";

const NAV_BUTTONS = [
  { label: "Overview", value: "Overview" },
  { label: "Infrastructure", value: "Infrastructure" },
  { label: "Institutional Capacity", value: "InstituitionalCapacity" },
  { label: "Sanitation", value: "Sanitation" },
  { label: "Indicators", value: "Indicators" },
];

const NavigationButtons = ({
  selectedButton,
  setSelectedButton,
  handleButtonClick,
  setCitySelection,
}) => {
  const width = useWindowWidth();
  // Derive boolean breakpoints — prevents re-renders on every resize pixel
  const isWide = width >= 340;
  const isTablet = width > 425;
  const { request } = useApi({ isPublic: !isAuthenticated() });
  const navigate = useNavigate();
  // Destructure to stable React setter refs for use in effect deps
  const { first: setCityFirst, second: setCitySecond } = setCitySelection;

  // State for city list and survey data
  const [cityList, setCityList] = useState([]);
  const [surveyList, setSurveyList] = useState([]);

  // State for district selection
  const [firstDistrict, setFirstDistrict] = useState({ name: "Cumilla", value: "Cumilla" });
  const [secondDistrict, setSecondDistrict] = useState(null);

  // State for first city dropdown options
  const [firstYearOptions, setFirstYearOptions] = useState([
    { surveyName: "Survey-2020", year: 2020 },
  ]);
  const [firstDataSourceOptions, setFirstDataSourceOptions] = useState([
    {
      surveyId: 1,
      name: "Survey-2020",
      assignedCities: 66,
      submittedCities: 66,
      startDate: "2018-01-01",
      endDate: "2020-01-01",
      questionnaireId: 1,
      year: 2020,
      value: 1,
      "option-disabled": false,
      group: "Surveys",
    },
  ]);
  const [firstIMISList, setFirstIMISList] = useState([]);

  // State for second city dropdown options (for comparison)
  const [secondYearOptions, setSecondYearOptions] = useState([]);
  const [secondDataSourceOptions, setSecondDataSourceOptions] = useState([]);
  const [secondIMISList, setSecondIMISList] = useState([]);

  // State for selected data - First City
  const [firstCityData, setFirstCityData] = useState({
    city: { id: 1317, name: "Cumilla", type: "MUNICIPALITY", value: 1317 },
    year: 2020,
    dataSource: [
      {
        surveyId: 1,
        name: "Survey-2020",
        assignedCities: 66,
        submittedCities: 66,
        startDate: "2018-01-01",
        endDate: "2020-01-01",
        questionnaireId: 1,
        year: 2020,
        value: 1,
        "option-disabled": false,
        group: "Surveys",
      },
    ],
  });

  // State for selected data - Second City (for comparison)
  const [secondCityData, setSecondCityData] = useState();

  // Extract unique districts from city list
  const districtOptions = useMemo(() => {
    if (!cityList || cityList.length === 0) return [];
    
    const uniqueDistricts = [...new Set(cityList.map(city => city.district))];
    return uniqueDistricts
      .filter(district => district) // Remove null/undefined
      .sort()
      .map(district => ({
        name: district,
        value: district,
      }));
  }, [cityList]);

  // Filter cities by selected district for first city
  const firstDistrictCities = useMemo(() => {
    if (!firstDistrict || !cityList) return cityList;
    const districtValue = typeof firstDistrict === 'object' && firstDistrict !== null ? firstDistrict.value : firstDistrict;
    return cityList.filter(city => city.district === districtValue);
  }, [cityList, firstDistrict]);

  // Filter cities by selected district for second city
  const secondDistrictCities = useMemo(() => {
    if (!secondDistrict || !cityList) return cityList;
    const districtValue = typeof secondDistrict === 'object' && secondDistrict !== null ? secondDistrict.value : secondDistrict;
    return cityList.filter(city => city.district === districtValue);
  }, [cityList, secondDistrict]);

  // Memoized dropdown options — prevent new array references on every render
  const firstCityOptions = useMemo(() =>
    firstDistrictCities?.map((city) => ({ ...city, name: city.name, value: city.id })) ?? [],
  [firstDistrictCities]);

  const firstYearDropdownOptions = useMemo(() => {
    const seen = new Set();
    return firstYearOptions
      .map((year) => ({ ...year, name: year.year, value: year.year }))
      .filter(({ name, value }) => {
        const key = `${name}-${value}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [firstYearOptions]);

  const secondCityOptions = useMemo(() =>
    secondDistrictCities?.map((city) => ({ ...city, name: city.name, value: city.id })) ?? [],
  [secondDistrictCities]);

  const secondYearDropdownOptions = useMemo(() => {
    const seen = new Set();
    return secondYearOptions
      .map((year) => ({ ...year, name: year.year, value: year.year }))
      .filter(({ name, value }) => {
        const key = `${name}-${value}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [secondYearOptions]);

  // Stable setter callbacks — no new references on every render
  const handleFirstDistrictChange = useCallback(
    (value) => setFirstDistrict(value),
    []
  );
  const handleFirstCityChange = useCallback(
    (value) => setFirstCityData({ city: value, year: "", dataSource: null }),
    []
  );
  const handleFirstYearChange = useCallback(
    (value) => setFirstCityData((prev) => ({ ...prev, year: value })),
    []
  );
  const handleFirstDataSourceChange = useCallback(
    (value) => setFirstCityData((prev) => ({ ...prev, dataSource: value })),
    []
  );
  const handleSecondDistrictChange = useCallback(
    (value) => setSecondDistrict(value),
    []
  );
  const handleSecondCityChange = useCallback(
    (value) => setSecondCityData((prev) => ({ ...prev, city: value })),
    []
  );
  const handleSecondYearChange = useCallback(
    (value) => setSecondCityData((prev) => ({ ...prev, year: value })),
    []
  );
  const handleSecondDataSourceChange = useCallback(
    (value) => setSecondCityData((prev) => ({ ...prev, dataSource: value })),
    []
  );

  // Pass to parent only when selection is complete — avoids parent re-renders on every intermediate cascade step
  useEffect(() => {
    if (firstCityData?.city && firstCityData?.year && firstCityData?.dataSource?.length) {
      setCityFirst(firstCityData);
    }
  }, [firstCityData, setCityFirst]);

  // For second city: propagate isComparison flag changes immediately,
  // but only propagate city data when the full selection is complete
  useEffect(() => {
    if (!secondCityData) return;
    const hasCity = !!secondCityData?.city;
    const isComplete = hasCity && secondCityData?.year && secondCityData?.dataSource?.length;
    if (!hasCity || isComplete) {
      setCitySecond(secondCityData);
    }
  }, [secondCityData, setCitySecond]);

  // Initial data fetch - Load city list and survey list on component mount
  useEffect(() => {
    const fetchSurveyList = async () => {
      const response = await fetchAllSurveyList(request);
      setSurveyList(response);
    };

    const fetchCityData = async () => {
      await fetchCityList(request, setCityList);
    };

    fetchCityData();
    fetchSurveyList();
  }, []);

  // Update first city data source options when year changes
  useEffect(() => {
    // Filter IMIS data based on selected year
    const imisDataOption = firstIMISList
      .filter((imis) => firstCityData?.year == imis.year)
      .map((item) => ({ ...item, group: "IMIS", name: "IMIS Data" }));

    // Combine survey list with IMIS data if available
    const updatedSurveyList =
      surveyList && imisDataOption && imisDataOption.length > 0
        ? [...surveyList, ...[imisDataOption[0]]]
        : surveyList;

    if (updatedSurveyList && updatedSurveyList.length) {
      // Process options and set disabled state based on year and availability
      const processedOptions = updatedSurveyList
        .map((item) => ({
          ...item,
          name: item.name,
          value: item.surveyId,
          "option-disabled":
            !(
              item.year === firstCityData?.year &&
              firstYearOptions?.find(
                (element) => element.surveyName === item.name
              )
            ) && !item?.name?.includes("IMIS"),
          group: item.name?.includes("IMIS") ? "IMIS" : "Surveys",
        }))
        .sort((a, b) => (a.group === "IMIS" ? -1 : 1));

      setFirstDataSourceOptions(processedOptions);

      // Validate current data source selection
      const currentData = firstCityData?.dataSource?.[0];
      const valid =
        currentData &&
        currentData.year === firstCityData.year &&
        processedOptions.some((opt) => opt.surveyId === currentData.surveyId);

      if (!valid) {
        // AUTO-SELECT: Select first non-disabled data source option
        const firstNonDisabled = processedOptions.find(
          (opt) => !opt["option-disabled"]
        );
        if (firstNonDisabled) {
          setFirstCityData((prev) => ({
            ...prev,
            dataSource: [firstNonDisabled],
          }));
        } else {
          setFirstCityData((prev) => ({ ...prev, dataSource: null }));
        }
      }
    }
  }, [surveyList, firstCityData?.year, firstYearOptions]);

  // Update second city data source options when year changes (for comparison)
  useEffect(() => {
    // Filter IMIS data based on selected year
    const imisDataOption = secondIMISList
      .filter((imis) => secondCityData?.year == imis.year)
      .map((item) => ({ ...item, group: "IMIS", name: "IMIS Data" }));

    // Combine survey list with IMIS data if available
    const updatedSurveyList =
      surveyList && imisDataOption && imisDataOption.length > 0
        ? [...surveyList, ...[imisDataOption[0]]]
        : surveyList;

    if (updatedSurveyList && updatedSurveyList.length) {
      const processedOptions = updatedSurveyList
        .map((item) => ({
          ...item,
          name: item.name,
          value: item.surveyId,
          "option-disabled":
            !(
              item.year == secondCityData?.year &&
              secondYearOptions?.find((element) => {
                return element.surveyName == item.name;
              })
            ) && !item?.name?.includes("IMIS"),
          group: item.name?.includes("IMIS") ? "IMIS" : "Surveys",
        }))
        .sort((a, b) => (a.group === "IMIS" ? -1 : 1));

      setSecondDataSourceOptions(processedOptions);

      // AUTO-SELECT: Select first non-disabled data source option for second city
      const firstNonDisabled = processedOptions.find(
        (opt) => !opt["option-disabled"]
      );
      if (firstNonDisabled) {
        setSecondCityData((prev) => ({
          ...prev,
          dataSource: [firstNonDisabled],
        }));
      } else {
        setSecondCityData((prev) => ({ ...prev, dataSource: null }));
      }
    }
  }, [surveyList, secondCityData?.year, secondYearOptions]);

  // Fetch year options when first city changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch survey/year list and IMIS list in parallel
        const [yearList, imisSurveyList] = await Promise.all([
          fetchLocationWiseSurveyandYearList(request, firstCityData?.city?.id),
          isAuthenticated()
            ? fetchIMISurveyList(request, firstCityData?.city?.name)
            : Promise.resolve([]),
        ]);

        setFirstIMISList(imisSurveyList);
        const combinedYearOptions = [...yearList, ...imisSurveyList];
        setFirstYearOptions(combinedYearOptions);

        // AUTO-SELECT: Automatically select the first available year
        if (combinedYearOptions.length > 0) {
          const firstYear = combinedYearOptions[0].year;
          setFirstCityData((prev) => ({
            ...prev,
            year: firstYear,
            dataSource: null, // Reset data source when year changes
          }));
        }
      } catch (error) {
        console.log("Error fetching first city data:", error);
      }
    };

    if (firstCityData?.city) {
      fetchData();
    }
  }, [firstCityData?.city]);

  // Fetch year options when second city changes (for comparison)
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch survey/year list and IMIS list in parallel
        const [yearList, imisSurveyList] = await Promise.all([
          fetchLocationWiseSurveyandYearList(request, secondCityData?.city?.id),
          isAuthenticated()
            ? fetchIMISurveyList(request, secondCityData?.city?.name)
            : Promise.resolve([]),
        ]);

        setSecondIMISList(imisSurveyList);
        const combinedYearOptions = [...yearList, ...imisSurveyList];
        setSecondYearOptions(combinedYearOptions);

        // AUTO-SELECT: Automatically select the first available year for second city
        if (combinedYearOptions.length > 0) {
          const firstYear = combinedYearOptions[0].year;
          setSecondCityData((prev) => ({
            ...prev,
            year: firstYear,
            dataSource: null, // Reset data source when year changes
          }));
        }
      } catch (error) {
        console.log("Error fetching second city data:", error);
      }
    };

    if (secondCityData?.city) {
      fetchData();
    }
  }, [secondCityData?.city]);

  // Validate first city year selection when year options change
  useEffect(() => {
    if (
      firstCityData?.year &&
      firstYearOptions?.length &&
      !firstYearOptions.some((opt) => opt.year === firstCityData.year)
    ) {
      // Reset year and data source if current year is not in options
      setFirstCityData((prev) => ({
        ...prev,
        year: "",
        dataSource: null,
      }));
    }
  }, [firstYearOptions]);

  // Reset second city data when year options change
  useEffect(() => {
    // This effect was resetting data unnecessarily, modified to only reset when needed
    if (secondCityData && secondYearOptions?.length > 0) {
      const currentYearExists = secondYearOptions.some(
        (opt) => opt.year === secondCityData.year
      );
      if (!currentYearExists) {
        setSecondCityData((prev) => ({
          ...prev,
          year: "",
          dataSource: null,
        }));
      }
    }
  }, [secondYearOptions]);

  // Handle first district change - reset city selection if not in filtered list
  useEffect(() => {
    if (!firstDistrict) return;
    
    const districtValue = typeof firstDistrict === 'object' && firstDistrict !== null ? firstDistrict.value : firstDistrict;
    
    if (districtValue && firstCityData?.city) {
      const cityExistsInDistrict = firstDistrictCities.some(
        city => city.id === firstCityData.city.id
      );
      
      if (!cityExistsInDistrict && firstDistrictCities.length > 0) {
        // Auto-select first city from the district
        const firstCity = firstDistrictCities[0];
        setFirstCityData({
          city: {
            ...firstCity,
            value: firstCity.id
          },
          year: "",
          dataSource: null
        });
      }
    }
  }, [firstDistrict, firstDistrictCities]);

  // Handle second district change - reset city selection if not in filtered list
  useEffect(() => {
    if (!secondDistrict) return;
    
    const districtValue = typeof secondDistrict === 'object' && secondDistrict !== null ? secondDistrict.value : secondDistrict;
    
    if (districtValue && secondCityData?.city) {
      const cityExistsInDistrict = secondDistrictCities.some(
        city => city.id === secondCityData.city.id
      );
      
      if (!cityExistsInDistrict && secondDistrictCities.length > 0) {
        // Auto-select first city from the district
        const firstCity = secondDistrictCities[0];
        setSecondCityData((prev) => ({
          ...prev,
          city: {
            ...firstCity,
            value: firstCity.id
          },
          year: "",
          dataSource: null
        }));
      }
    }
  }, [secondDistrict, secondDistrictCities]);

  // Memoized styles — recompute only when breakpoint crosses, not on every pixel
  const dropDownStyle = useMemo(() => ({
    width: isWide ? "9rem" : "7rem",
    backgroundColor: "#2F4B7C",
    color: "white",
    paddingLeft: 0,
    fontSize: "16px",
  }), [isWide]);

  const dataSourceDropDownStyle = useMemo(() => ({
    ...dropDownStyle,
    width: isWide ? "14rem" : "11rem",
  }), [dropDownStyle, isWide]);

  // Stable nav button handlers — recreated only if handleButtonClick changes
  const navButtonHandlers = useMemo(
    () => NAV_BUTTONS.reduce(
      (map, btn) => ({ ...map, [btn.value]: () => handleButtonClick(btn.value) }),
      {}
    ),
    [handleButtonClick]
  );

  return (
    <div className="w-full relative bg-[#ffffff] bg-opacity-50 p-1">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="w-full sm:w-auto flex justify-between flex-wrap gap-3">
          {/* Navigation buttons - Only show when data is selected */}
          {(firstCityData?.dataSource || secondCityData?.dataSource) && (
            <div className="w-full sm:w-auto flex flex-wrap gap-3">
              {NAV_BUTTONS.map((button) => (
                <Button
                  key={button.value}
                  type="small_bordered"
                  label={button.label}
                  backgroundColor={
                    selectedButton === button.value ? "#2F4B7C" : "white"
                  }
                  textColor={
                    selectedButton === button.value ? "white" : "#2F4B7C"
                  }
                  handler={navButtonHandlers[button.value]}
                />
              ))}
            </div>
          )}
        </div>

        {/* Single City vs Compare Cities toggle */}
        <div className="text-sm 2xl:text-base">
          {firstCityData &&
            firstCityData.city &&
            firstCityData?.dataSource &&
            firstCityData?.dataSource?.length &&
            isTablet ? (
            <div className="inline-flex ">
              <div className="align-middle items-center gap-3  rounded-sm  mr-3 flex">
                <ExportButton cityId={firstCityData.city.id} year={firstCityData.year} disabled={!firstCityData.city.id || secondCityData?.isComparison} />
              </div>
              <div className="inline-flex border border-[#7ddbff] rounded-sm h-9 justify-end">
                <button
                  onClick={() => {
                    setSecondCityData((prev) => ({
                      ...prev,
                      isComparison: false,
                    }));
                    navigate("/city-profile");
                    setSelectedButton("Overview");
                  }}
                  className={`px-2 text-sm font-medium transition-all ${!secondCityData?.isComparison
                      ? "bg-[#2F4B7C] text-white"
                      : "text-[#2F4B7C] bg-white hover:bg-[#2F4B7C]/10"
                    }`}
                >
                  Single City
                </button>
                <button
                  onClick={() => {
                    setSecondCityData((prev) => ({
                      ...prev,
                      isComparison: true,
                    }));
                    navigate("/city-comparison");
                    setSelectedButton("Overview");
                  }}
                  className={`px-2 text-sm font-medium transition-all ${secondCityData?.isComparison
                      ? "bg-[#2F4B7C] text-white"
                      : "text-[#2F4B7C] bg-white hover:bg-[#2F4B7C]/10"
                    }`}
                >
                  Compare Cities
                </button>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>

      {/* Dropdown Section */}
      <div className={`mt-2 ${secondCityData?.isComparison ? "flex gap-3" : "flex flex-wrap gap-3"}`}>
        {/* First City Dropdowns */}
        <div className={`flex flex-wrap gap-3 ${secondCityData?.isComparison ? "w-[calc(50%-0.375rem)]" : "w-full"}`}>
          <DropDown
            setter={handleFirstDistrictChange}
            placeholder="Select District"
            value={firstDistrict}
            options={districtOptions}
            style={dropDownStyle}
            baggage={true}
          />

          <DropDown
            setter={handleFirstCityChange}
            placeholder="Select City"
            value={firstCityData?.city}
            options={firstCityOptions}
            style={dropDownStyle}
            baggage={true}
          />

          <DropDown
            searchAble={false}
            setter={handleFirstYearChange}
            placeholder="Select Year"
            value={firstCityData?.year}
            options={firstYearDropdownOptions}
            style={dropDownStyle}
          />

          <DropDownGrouped
            setter={handleFirstDataSourceChange}
            placeholder="Select Data Source"
            value={firstCityData?.dataSource}
            options={firstDataSourceOptions}
            style={dataSourceDropDownStyle}
            baggage={true}
            multiple={true}
          />
        </div>

        {/* Second City Dropdowns (for comparison) */}
        {secondCityData?.isComparison && (
          <div className="flex flex-wrap gap-3 w-[calc(50%-0.375rem)]">
            <DropDown
              setter={handleSecondDistrictChange}
              placeholder="Select District"
              value={secondDistrict}
              options={districtOptions}
              style={dropDownStyle}
              baggage={true}
            />

            <DropDown
              setter={handleSecondCityChange}
              placeholder="Select City"
              value={secondCityData?.city}
              options={secondCityOptions}
              style={dropDownStyle}
              baggage={true}
            />

            <DropDown
              searchAble={false}
              setter={handleSecondYearChange}
              placeholder="Select Year"
              value={secondCityData?.year}
              options={secondYearDropdownOptions}
              style={dropDownStyle}
            />

            <DropDownGrouped
              setter={handleSecondDataSourceChange}
              placeholder="Select Data Source"
              value={secondCityData?.dataSource}
              options={secondDataSourceOptions}
              style={dataSourceDropDownStyle}
              baggage={true}
              multiple={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NavigationButtons;